import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import { useCRM, useCRMCollections } from '../utils/CRMContext';
import {
  OnboardingItem,
  ProjectOnboarding,
  ProjectPhase,
  ProjectTask,
  TimberProject,
  EnhancedDeal,
} from '../types';
import AppHeader from '../components/ui/AppHeader';
import ArchitectHeader from '../components/pm/ArchitectHeader';
import StatusPill from '../components/pm/StatusPill';
import PriorityBadge from '../components/pm/PriorityBadge';
import TaskDetailPanel from '../components/pm/TaskDetailPanel';
import GroupCommentsPopout from '../components/pm/GroupCommentsPopout';
import CompleteTaskActualsModal from '../components/pm/CompleteTaskActualsModal';
import { useCommentsMovedTour } from '../components/pm/CommentsMovedTour';
import CommentsHelpButton from '../components/pm/CommentsHelpButton';
import { ProjectUploadModalProvider } from '../components/pm/ProjectUploadModalProvider';
import {
  buildGroupContextForTask,
  buildGroupMemberTasks,
  aggregateGroupComments,
  groupHasUnread,
  type GroupCommentContext,
} from '../utils/groupComments';
import { markGroupAsRead } from '../utils/taskReadReceipts';
import { collectTaskCommentMentionProjectIds } from '../utils/projectHubRollups';
import { subscribeToAnnotationsForProjectIds } from '../utils/pdfAnnotationService';
import type { PdfAnnotation } from '../types';
import {
  Flag, AlertTriangle, Clock, Calendar, ChevronRight,
  ChevronDown, MapPin, User,
  Building2, FolderOpen, ArrowLeft,
  MessageSquare, Users, LayoutGrid,
} from 'lucide-react';
import '../components/pm/pm-shared.css';
import './ArchitectPortal.css';
import {
  subscribeToReadReceipts,
  markTaskAsRead,
  ReadReceiptMap,
} from '../utils/taskReadReceipts';
import {
  ACTION_REQUIRED_EXTERNAL_SENTINEL,
  isActionRequiredExternal,
} from '../utils/actionRequiredDisplay';
import { applyActionRequiredSideEffects } from '../utils/actionRequiredWorkflow';
import {
  applyTaskCompletion,
  resolveTaskCompletionPrompt,
} from '../utils/taskCompletionPrompt';
import { canViewActionRequiredWaitAnalytics, isCrmTestUser, isDesignerRole } from '../utils/userRoles';
import { getAssignedDesigner, getExternalArchitectName } from '../utils/dealDesignerArchitect';
import { listArchitects } from '../utils/architectProjectAccess';
import StaffPortalViewAs from '../components/pm/StaffPortalViewAs';
import {
  parseStaffPortalPreview,
  staffPortalPreviewExitHref,
  staffPortalPreviewHref,
  staffPortalPreviewSearch,
} from '../utils/staffPortalPreview';
import {
  isOnboardingBridgeTaskId,
  onboardingItemToProjectTask,
  onboardingTaskIdToItemId,
  normalizeOnboardingDepartment,
  parseOnboardingBridgeTaskId,
  resolveDepartmentFromTaskGroupName,
  resolveTaskGroupIdForDepartment,
} from '../utils/onboardingTaskBridge';
import { taskInvolvesUser } from '../utils/projectTaskUsers';
import { recordTaskActivityChanges } from '../utils/taskActivityLogger';
import { createTaskAssignmentNotification } from '../utils/taskAssignmentNotification';
import { useArchEngineerWorkQueues } from '../hooks/useArchEngineerWorkQueues';
import {
  compareProjectsByWorkQueueThenNumber,
  sortTimberProjectsForHubWorkQueue,
} from '../utils/archEngineerWorkQueue';
import { formatRelativeLocalCalendarDay, isTaskDueDatePastLocalCalendar } from '../utils/taskSchedule';
import { getTaskDetailPanelProjectContext, getPortalProjectCardTitle } from '../utils/projectName';
import { buildArchitectInvolvementGroups } from '../utils/architectProjectInvolvement';
import { engineerIsInvolvedInProject } from '../utils/archEngineerProjectHubAccess';
import { subscribeToSettings, type AppSettings } from '../utils/appSettings';
import { buildInspirationBoardPath } from '../utils/notificationDeepLinks';

interface ProjectGroup {
  project: TimberProject;
  deal: EnhancedDeal | undefined;
  myTasks: ProjectTask[];
  actionCount: number;
  overdueCount: number;
}

const SUBSTAGE_LABELS: Record<string, string> = {
  'payment-requested': 'Payment Requested',
  'in-design': 'In Design',
  'design-review': 'Design Review',
  'revisions': 'Revisions',
  'design-complete': 'Design Complete',
};

const SUBSTAGE_COLORS: Record<string, string> = {
  'payment-requested': '#F59E0B',
  'in-design': '#3B82F6',
  'design-review': '#8B5CF6',
  'revisions': '#F97316',
  'design-complete': '#22C55E',
};

const STATUS_LABELS: Record<string, string> = {
  'planning': 'Planning',
  'design': 'In Design',
  'production': 'Production',
  'installation': 'Installation',
  'completed': 'Completed',
  'on-hold': 'On Hold',
};

const STATUS_COLORS: Record<string, string> = {
  'planning': '#94A3B8',
  'design': '#3B82F6',
  'production': '#F59E0B',
  'installation': '#8B5CF6',
  'completed': '#22C55E',
  'on-hold': '#EF4444',
};

function resolveOnboardingBridgeProjectId(
  taskId: string,
  projectOnboardings: ProjectOnboarding[],
): string | null {
  const parsed = parseOnboardingBridgeTaskId(taskId);
  if (parsed?.projectId) return parsed.projectId;
  const itemId = onboardingTaskIdToItemId(taskId);
  const ob = projectOnboardings.find((o) => o.items.some((it) => it.id === itemId));
  return ob?.projectId ?? null;
}

function inspirationBoardHrefForTask(
  task: ProjectTask,
  timberProjects: TimberProject[],
  enhancedDeals: EnhancedDeal[],
): string | null {
  if (!task.projectId) return null;
  const timber = timberProjects.find((p) => p.id === task.projectId);
  const dealId = timber?.dealId;
  if (!dealId) return null;
  const leadId = enhancedDeals.find((d) => d.id === dealId)?.leadId;
  if (!leadId) return null;
  return buildInspirationBoardPath(leadId, dealId);
}

const ArchitectPortal: React.FC = () => {
  const { user } = useAuth();
  const {
    projectTasks,
    projectOnboardings,
    timberProjects,
    enhancedDeals,
    taskGroups,
    taskComments,
    crmUsers,
    updateProjectTask,
    deleteProjectTask,
    updateOnboardingItem,
    deleteOnboardingItem,
    getOnboardingForProject,
    createTaskActivity,
  } = useCRM();
  useCRMCollections(['projectTasks', 'taskGroups', 'taskComments', 'projectOnboardings']);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [selectedTask, setSelectedTask] = useState<ProjectTask | null>(null);
  const [arActionMenuTaskId, setArActionMenuTaskId] = useState<string | null>(null);
  const [arActionMenuStep, setArActionMenuStep] = useState<'list' | 'external'>('list');
  const [arActionExternalDraft, setArActionExternalDraft] = useState('');
  const arActionMenuHostRef = useRef<HTMLDivElement | null>(null);
  const [showCompletedTasks, setShowCompletedTasks] = useState(false);
  const [pendingCompletionTask, setPendingCompletionTask] = useState<null | {
    taskId: string;
    title: string;
    scheduledStart: Date;
    scheduledEnd: Date;
    projectId: string;
    isOnboarding: boolean;
  }>(null);

  useEffect(() => {
    if (!arActionMenuTaskId) return;
    const handler = (e: MouseEvent) => {
      if (arActionMenuHostRef.current && !arActionMenuHostRef.current.contains(e.target as Node)) {
        setArActionMenuTaskId(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [arActionMenuTaskId]);

  useEffect(() => {
    setArActionMenuStep('list');
    setArActionExternalDraft('');
  }, [arActionMenuTaskId]);

  const realUserId = user?.id || '';
  const userName = user?.name || '';
  const realIsDesigner = isDesignerRole(user?.role);
  const realIsEngineer = user?.role === 'engineer';
  const preview = !realIsDesigner && !realIsEngineer
    ? parseStaffPortalPreview(searchParams)
    : null;
  const viewAsExternalId = preview?.uid ?? null;
  const previewKind = preview?.kind ?? null;

  const viewingAsExternal = !!viewAsExternalId;
  const userId = viewingAsExternal ? viewAsExternalId! : realUserId;
  const isEngineerView = realIsEngineer || previewKind === 'engineer';
  const isPortalChrome = realIsDesigner || realIsEngineer || viewingAsExternal;

  // Per-task read receipts → drives the unread dot on the comments button.
  // Subscribes against the real signed-in user (not the impersonation target);
  // unread state belongs to the actual viewer regardless of view-as mode.
  const [readReceipts, setReadReceipts] = useState<ReadReceiptMap>({});
  const [engineeringFirms, setEngineeringFirms] = useState<AppSettings['engineeringFirms']>([]);
  useEffect(() => subscribeToSettings((s) => setEngineeringFirms(s.engineeringFirms || [])), []);
  useEffect(() => {
    if (!realUserId) return;
    return subscribeToReadReceipts(realUserId, setReadReceipts);
  }, [realUserId]);

  // Per-row "open comments" shortcut → opens the consolidated group thread for
  // the task's group. Date.now() token ensures repeat clicks re-fire the popup.
  const [popoutCommentsToken, setPopoutCommentsToken] = useState<number | null>(null);
  const [groupCommentsContext, setGroupCommentsContext] = useState<GroupCommentContext | null>(null);

  const handleCloseCommentsPopout = useCallback(() => {
    setGroupCommentsContext(null);
    setPopoutCommentsToken(null);
  }, []);

  const handleOpenTaskComments = useCallback((task: ProjectTask, options?: { markAsRead?: boolean }) => {
    const ctx = buildGroupContextForTask(task, timberProjects, taskGroups, projectTasks, projectOnboardings);
    if (!ctx) return false;
    setGroupCommentsContext(ctx);
    // The walkthrough opens a thread purely to demo the UI — don't clear unread state then.
    if (realUserId && options?.markAsRead !== false) void markGroupAsRead(realUserId, ctx.groupKey);
    setPopoutCommentsToken(Date.now());
    return true;
  }, [realUserId, timberProjects, taskGroups, projectTasks, projectOnboardings]);

  /** Open a representative task's group thread for the "comments moved" walkthrough (no read side-effects). */
  const openCommentsForTour = useCallback((): boolean => {
    const demoTask = projectTasks.find((t) =>
      buildGroupContextForTask(t, timberProjects, taskGroups, projectTasks, projectOnboardings),
    );
    if (!demoTask) return false;
    return handleOpenTaskComments(demoTask, { markAsRead: false });
  }, [projectTasks, timberProjects, taskGroups, projectOnboardings, handleOpenTaskComments]);

  // First-visit "comments moved" walkthrough — spotlights a task's comment button + the popup tabs.
  const { startTour: startCommentsTour } = useCommentsMovedTour({
    enabled: !!realUserId && projectTasks.length > 0,
    surface: 'task',
    openDemo: openCommentsForTour,
    closeDemo: handleCloseCommentsPopout,
  });

  const commentsPopoutDealId = useMemo(() => {
    if (!groupCommentsContext) return undefined;
    return timberProjects.find((p) => p.id === groupCommentsContext.primaryProjectId)?.dealId;
  }, [groupCommentsContext, timberProjects]);

  // Subscribe to the open group's project annotations for the PDF Comments tab.
  const [groupCommentsAnnotations, setGroupCommentsAnnotations] = useState<PdfAnnotation[]>([]);
  useEffect(() => {
    const pid = groupCommentsContext?.primaryProjectId;
    if (!pid) {
      setGroupCommentsAnnotations([]);
      return;
    }
    return subscribeToAnnotationsForProjectIds([pid], (byProject) => {
      setGroupCommentsAnnotations(byProject.get(pid) ?? []);
    });
  }, [groupCommentsContext?.primaryProjectId]);

  const groupCommentMemberTasks = useMemo(() => {
    if (!groupCommentsContext) return [];
    return buildGroupMemberTasks(
      groupCommentsContext,
      projectTasks,
      timberProjects,
      taskGroups,
      projectOnboardings,
    );
  }, [groupCommentsContext, projectTasks, timberProjects, taskGroups, projectOnboardings]);

  const teamMembers = useMemo(() =>
    crmUsers.filter(u =>
      !isCrmTestUser(u)
      && (u.role === 'employee' || u.role === 'admin' || isDesignerRole(u.role) || u.role === 'engineer')
    ),
    [crmUsers]
  );

  const architects = useMemo(() =>
    crmUsers.filter(u => isDesignerRole(u.role) && u.isActive && !isCrmTestUser(u)),
    [crmUsers]
  );

  const engineers = useMemo(() =>
    crmUsers.filter(u => u.role === 'engineer' && u.isActive && !isCrmTestUser(u)),
    [crmUsers]
  );

  const partnerArchitects = useMemo(() => listArchitects(crmUsers), [crmUsers]);

  const externalUsers = useMemo(() => [...architects, ...engineers], [architects, engineers]);
  const canShowViewAs = !realIsDesigner && !realIsEngineer && (externalUsers.length > 0 || partnerArchitects.length > 0);

  const viewAsExternalUser = viewAsExternalId
    ? externalUsers.find(u => u.uid === viewAsExternalId)
    : null;
  const viewAsExternalName = viewAsExternalUser?.displayName || null;

  const buildGroups = useCallback((phase: ProjectPhase) => {
    const now = new Date();
    const groups: ProjectGroup[] = [];

    for (const project of timberProjects) {
      if (!project.id) continue;
      if ((project.phase || 'contracted') !== phase) continue;
      if (project.status === 'completed') continue;

      const deal = project.dealId
        ? enhancedDeals.find(d => d.id === project.dealId)
        : undefined;

      const pid = project.id!;
      const groupsForProject = taskGroups.filter((g) => g.projectId === pid);
      const onboardingDoc = projectOnboardings.find((ob) => ob.projectId === pid);
      const onboardingMirrored: ProjectTask[] =
        onboardingDoc?.items?.length
          ? onboardingDoc.items.map((it) => onboardingItemToProjectTask(it, pid, groupsForProject))
          : [];
      const firestoreForProject = projectTasks.filter((t) => t.projectId === pid);
      const mergedForProject = [...onboardingMirrored, ...firestoreForProject];

      const myTasks = mergedForProject.filter((t) => taskInvolvesUser(t, userId));
      const myActiveTasks = myTasks.filter((t) => t.status !== 'completed');

      // Active work drives the list; with "show completed", also list projects where you only have finished tasks.
      if (myActiveTasks.length === 0 && (!showCompletedTasks || myTasks.length === 0)) continue;

      const actionCount = myTasks.filter(t =>
        t.actionRequired === userId && t.status !== 'completed'
      ).length;

      const overdueCount = myTasks.filter((t) =>
        t.status !== 'completed' && isTaskDueDatePastLocalCalendar(new Date(t.endDate), now),
      ).length;

      groups.push({ project, deal, myTasks, actionCount, overdueCount });
    }

    groups.sort((a, b) => {
      if (a.actionCount !== b.actionCount) return b.actionCount - a.actionCount;
      const aActive = a.myTasks.filter((t) => t.status !== 'completed').length;
      const bActive = b.myTasks.filter((t) => t.status !== 'completed').length;
      return bActive - aActive;
    });

    return groups;
  }, [timberProjects, enhancedDeals, projectTasks, projectOnboardings, taskGroups, userId, showCompletedTasks]);

  const estimatingGroups = useMemo(() => buildGroups('estimating'), [buildGroups]);
  const preconGroups = useMemo(() => buildGroups('preconstruction'), [buildGroups]);
  const contractedGroups = useMemo(() => buildGroups('contracted'), [buildGroups]);

  const preconStats = useMemo(
    () => ({
      total: preconGroups.length,
      action: preconGroups.reduce((s, g) => s + g.actionCount, 0),
      overdue: preconGroups.reduce((s, g) => s + g.overdueCount, 0),
      tasks: preconGroups.reduce((s, g) => s + g.myTasks.filter((t) => t.status !== 'completed').length, 0),
    }),
    [preconGroups],
  );

  const contractedStats = useMemo(
    () => ({
      total: contractedGroups.length,
      action: contractedGroups.reduce((s, g) => s + g.actionCount, 0),
      overdue: contractedGroups.reduce((s, g) => s + g.overdueCount, 0),
      tasks: contractedGroups.reduce((s, g) => s + g.myTasks.filter((t) => t.status !== 'completed').length, 0),
    }),
    [contractedGroups],
  );

  const estimatingStats = useMemo(
    () => ({
      total: estimatingGroups.length,
      action: estimatingGroups.reduce((s, g) => s + g.actionCount, 0),
      overdue: estimatingGroups.reduce((s, g) => s + g.overdueCount, 0),
      tasks: estimatingGroups.reduce((s, g) => s + g.myTasks.filter((t) => t.status !== 'completed').length, 0),
    }),
    [estimatingGroups],
  );

  const allGroups = useMemo(
    () => [...estimatingGroups, ...preconGroups, ...contractedGroups],
    [estimatingGroups, preconGroups, contractedGroups],
  );

  const combinedStats = useMemo(
    () => ({
      total: estimatingGroups.length + preconGroups.length + contractedGroups.length,
      action: estimatingStats.action + preconStats.action + contractedStats.action,
      overdue: estimatingStats.overdue + preconStats.overdue + contractedStats.overdue,
      tasks: estimatingStats.tasks + preconStats.tasks + contractedStats.tasks,
    }),
    [
      estimatingGroups.length,
      preconGroups.length,
      contractedGroups.length,
      estimatingStats,
      preconStats,
      contractedStats,
    ],
  );

  const stats = combinedStats;

  const queuesByUserId = useArchEngineerWorkQueues();
  const dealsByIdForPmRank = useMemo(
    () => new Map(enhancedDeals.filter((d) => d.id).map((d) => [d.id!, d] as const)),
    [enhancedDeals],
  );
  const activeGroupsSorted = useMemo(() => {
    const queueIds = queuesByUserId[userId] ?? [];
    return [...allGroups].sort((a, b) =>
      compareProjectsByWorkQueueThenNumber(a.project, b.project, queueIds, dealsByIdForPmRank),
    );
  }, [allGroups, queuesByUserId, userId, dealsByIdForPmRank]);

  /** Ranks 1…n match Project Hub work queue (precon + under contract in one list), not per tab. */
  const hubWorkQueueSorted = useMemo(
    () =>
      sortTimberProjectsForHubWorkQueue(
        timberProjects,
        userId,
        projectTasks,
        taskGroups,
        projectOnboardings,
        queuesByUserId[userId] ?? [],
        dealsByIdForPmRank,
      ),
    [timberProjects, userId, projectTasks, taskGroups, projectOnboardings, queuesByUserId, dealsByIdForPmRank],
  );

  const pmPriorityRankByProjectId = useMemo(() => {
    const m = new Map<string, number>();
    hubWorkQueueSorted.forEach((p, i) => {
      if (p.id) m.set(p.id, i + 1);
    });
    return m;
  }, [hubWorkQueueSorted]);

  const activeProjectIds = useMemo(
    () => new Set(activeGroupsSorted.map((g) => g.project.id).filter(Boolean) as string[]),
    [activeGroupsSorted],
  );

  const involvementGroups = useMemo(
    () =>
      buildArchitectInvolvementGroups(
        timberProjects,
        enhancedDeals,
        projectTasks,
        projectOnboardings,
        taskGroups,
        userId,
        crmUsers,
        activeProjectIds,
      ),
    [
      timberProjects,
      enhancedDeals,
      projectTasks,
      projectOnboardings,
      taskGroups,
      userId,
      crmUsers,
      activeProjectIds,
    ],
  );

  const mentionedProjectGroups = useMemo(() => {
    const mentioned = collectTaskCommentMentionProjectIds(taskComments, timberProjects, userId);
    const shown = new Set<string>(activeProjectIds);
    for (const group of involvementGroups) {
      if (group.project.id) shown.add(group.project.id);
    }
    const groups: ProjectGroup[] = [];
    for (const project of timberProjects) {
      if (!project.id || project.status === 'completed' || shown.has(project.id)) continue;
      if (!mentioned.has(project.id)) continue;
      const deal = project.dealId ? enhancedDeals.find((d) => d.id === project.dealId) : undefined;
      groups.push({ project, deal, myTasks: [], actionCount: 0, overdueCount: 0 });
    }
    return groups;
  }, [taskComments, timberProjects, userId, activeProjectIds, involvementGroups, enhancedDeals]);

  const engineerFirmGroups = useMemo(() => {
    if (!isEngineerView || !userId) return [];
    const shown = new Set<string>(activeProjectIds);
    for (const group of involvementGroups) {
      if (group.project.id) shown.add(group.project.id);
    }
    for (const group of mentionedProjectGroups) {
      if (group.project.id) shown.add(group.project.id);
    }
    const groups: ProjectGroup[] = [];
    for (const project of timberProjects) {
      if (!project.id || shown.has(project.id)) continue;
      if (!engineerIsInvolvedInProject(
        project,
        userId,
        projectTasks,
        taskGroups,
        projectOnboardings,
        { crmUsers, engineeringFirms: engineeringFirms ?? [] },
      )) continue;
      const deal = project.dealId ? enhancedDeals.find((d) => d.id === project.dealId) : undefined;
      groups.push({ project, deal, myTasks: [], actionCount: 0, overdueCount: 0 });
    }
    return groups;
  }, [
    isEngineerView,
    userId,
    activeProjectIds,
    involvementGroups,
    mentionedProjectGroups,
    timberProjects,
    projectTasks,
    taskGroups,
    projectOnboardings,
    crmUsers,
    engineeringFirms,
    enhancedDeals,
  ]);

  const portalExtraGroups = useMemo(
    () => [...involvementGroups, ...mentionedProjectGroups, ...engineerFirmGroups],
    [involvementGroups, mentionedProjectGroups, engineerFirmGroups],
  );

  const handleUpdateTask = useCallback(async (id: string, updates: Partial<ProjectTask>) => {
    const completionPrompt = resolveTaskCompletionPrompt(id, updates, {
      projectTasks,
      taskGroups,
      projectOnboardings,
      getOnboardingForProject,
      fallbackProjectId: resolveOnboardingBridgeProjectId(id, projectOnboardings)
        || projectTasks.find((t) => t.id === id)?.projectId
        || undefined,
    });
    if (completionPrompt) {
      setPendingCompletionTask(completionPrompt);
      return;
    }

    if (isOnboardingBridgeTaskId(id)) {
      const projectId = resolveOnboardingBridgeProjectId(id, projectOnboardings);
      if (!projectId || !user?.id) return;

      const itemId = onboardingTaskIdToItemId(id);
      const actor = userName || user.email || 'Unknown';
      const groupsForProject = taskGroups.filter((g) => g.projectId === projectId);
      const obItem = getOnboardingForProject(projectId)?.items?.find((it) => it.id === itemId);
      const beforeOnboarding =
        obItem ? onboardingItemToProjectTask(obItem, projectId, groupsForProject) : undefined;
      const onboardingTask = beforeOnboarding;
      const previouslyAssigned = onboardingTask?.assignedTo ?? [];

      const { groupId: movedToGroupId, ...effective } = updates;
      let eff = { ...effective };
      if (onboardingTask) {
        eff = applyActionRequiredSideEffects(onboardingTask, eff);
      }

      const patches: Partial<OnboardingItem> = {};
      if (movedToGroupId !== undefined) {
        patches.taskGroupId = movedToGroupId || undefined;
      }
      if (eff.status !== undefined) {
        patches.completed = eff.status === 'completed';
        patches.taskStatus = eff.status;
      } else if (eff.progress !== undefined) {
        patches.completed = (eff.progress ?? 0) >= 100;
        if ((eff.progress ?? 0) >= 100) patches.taskStatus = 'completed';
      }
      if (eff.title !== undefined) patches.title = eff.title;
      if (eff.description !== undefined) patches.description = eff.description;
      if (eff.assignedTo !== undefined) {
        patches.assignedTo = eff.assignedTo;
        const uid = eff.assignedTo[0];
        if (uid) {
          patches.assigneeId = uid;
          patches.assigneeName = crmUsers.find((c) => c.uid === uid)?.displayName;
        } else {
          patches.assigneeId = undefined;
          patches.assigneeName = undefined;
        }
      }
      if (eff.progress !== undefined) patches.progress = eff.progress;
      if (eff.priority !== undefined) patches.priority = eff.priority;
      if (eff.startDate !== undefined) patches.startDate = eff.startDate;
      if (eff.endDate !== undefined) patches.endDate = eff.endDate;
      if (eff.dependencyEdges !== undefined) patches.dependencyEdges = eff.dependencyEdges;
      if (eff.subtasks !== undefined) patches.subtasks = eff.subtasks;
      if ('actionRequired' in eff) patches.actionRequired = eff.actionRequired;
      if ('actionRequiredNote' in eff) patches.actionRequiredNote = eff.actionRequiredNote;
      if (eff.onCriticalPath !== undefined) patches.onCriticalPath = eff.onCriticalPath;
      if (eff.department !== undefined) {
        patches.department = eff.department;
        if (movedToGroupId === undefined) {
          const gid = resolveTaskGroupIdForDepartment(
            normalizeOnboardingDepartment(eff.department),
            projectId,
            groupsForProject,
          );
          if (gid) patches.taskGroupId = gid;
        }
      }
      if (
        movedToGroupId !== undefined &&
        movedToGroupId &&
        eff.department === undefined
      ) {
        const g = groupsForProject.find((x) => x.id === movedToGroupId);
        const dept = g ? resolveDepartmentFromTaskGroupName(g.name) : undefined;
        if (dept) patches.department = dept;
      }
      if (Object.keys(patches).length === 0) return;

      await updateOnboardingItem(projectId, itemId, patches, actor);

      if (beforeOnboarding) {
        const logUpdates: Partial<ProjectTask> = {};
        if (eff.status !== undefined) logUpdates.status = eff.status;
        if (eff.title !== undefined) logUpdates.title = eff.title;
        if (eff.description !== undefined) logUpdates.description = eff.description;
        if (eff.assignedTo !== undefined) logUpdates.assignedTo = eff.assignedTo;
        if (eff.progress !== undefined) logUpdates.progress = eff.progress;
        if (eff.priority !== undefined) logUpdates.priority = eff.priority;
        if (eff.startDate !== undefined) logUpdates.startDate = eff.startDate;
        if (eff.endDate !== undefined) logUpdates.endDate = eff.endDate;
        if (eff.dependencyEdges !== undefined) logUpdates.dependencyEdges = eff.dependencyEdges;
        if (eff.subtasks !== undefined) logUpdates.subtasks = eff.subtasks;
        if ('actionRequired' in eff) logUpdates.actionRequired = eff.actionRequired;
        if ('actionRequiredNote' in eff) logUpdates.actionRequiredNote = eff.actionRequiredNote;
        if (eff.onCriticalPath !== undefined) logUpdates.onCriticalPath = eff.onCriticalPath;
        if (eff.department !== undefined) logUpdates.department = eff.department;
        if (movedToGroupId !== undefined) logUpdates.groupId = movedToGroupId;
        if (Object.keys(logUpdates).length > 0) {
          recordTaskActivityChanges({
            before: beforeOnboarding,
            updates: logUpdates,
            taskId: id,
            actorId: user.id,
            actorName: actor,
            teamMembers: crmUsers,
            createTaskActivity,
          });
        }
      }

      if (eff.assignedTo && onboardingTask) {
        const newlyAssigned = eff.assignedTo.filter((uid) => !previouslyAssigned.includes(uid));
        const project = timberProjects.find((p) => p.id === onboardingTask.projectId);
        const projectName = project?.name ?? 'Project';
        for (const assigneeId of newlyAssigned) {
          if (assigneeId === realUserId) continue;
          createTaskAssignmentNotification({
            assigneeUserId: assigneeId,
            taskId: id,
            taskTitle: onboardingTask.title,
            projectId: onboardingTask.projectId,
            projectName,
            assignedByName: userName || 'Someone',
          });
        }
      }

      if (selectedTask && selectedTask.id === id && updates.assignedTo) {
        if (!updates.assignedTo.includes(userId)) {
          setSelectedTask(null);
        }
      }
      return;
    }

    const task = projectTasks.find((t) => t.id === id);
    const previouslyAssigned = task?.assignedTo ?? [];
    const payload = task ? applyActionRequiredSideEffects(task, { ...updates }) : { ...updates };

    await updateProjectTask(id, payload);

    if (task && user?.id) {
      recordTaskActivityChanges({
        before: task,
        updates: payload,
        taskId: id,
        actorId: user.id,
        actorName: userName || user?.email || 'Unknown',
        teamMembers: crmUsers,
        createTaskActivity,
      });
    }

    if (payload.assignedTo && task) {
      const newlyAssigned = payload.assignedTo.filter((uid) => !previouslyAssigned.includes(uid));
      const project = timberProjects.find((p) => p.id === task.projectId);
      const projectName = project?.name ?? 'Project';

      for (const assigneeId of newlyAssigned) {
        if (assigneeId === realUserId) continue;
        createTaskAssignmentNotification({
          assigneeUserId: assigneeId,
          taskId: id,
          taskTitle: task.title,
          projectId: task.projectId,
          projectName,
          assignedByName: userName || 'Someone',
        });
      }
    }

    if (selectedTask && selectedTask.id === id && payload.assignedTo) {
      if (!payload.assignedTo.includes(userId)) {
        setSelectedTask(null);
      }
    }
  }, [
    updateProjectTask,
    updateOnboardingItem,
    projectTasks,
    projectOnboardings,
    taskGroups,
    timberProjects,
    userId,
    realUserId,
    userName,
    selectedTask,
    user,
    crmUsers,
    createTaskActivity,
    getOnboardingForProject,
  ]);

  const handleRequestGroupTaskComplete = useCallback(
    (taskId: string) => {
      void handleUpdateTask(taskId, { status: 'completed', progress: 100 });
    },
    [handleUpdateTask],
  );

  const handleDeleteTask = useCallback(
    (taskId: string) => {
      if (isOnboardingBridgeTaskId(taskId)) {
        const projectId = resolveOnboardingBridgeProjectId(taskId, projectOnboardings);
        if (!projectId) {
          console.warn('[ArchitectPortal] Delete onboarding task: unknown project', taskId);
          return;
        }
        const obItemId = onboardingTaskIdToItemId(taskId);
        void deleteOnboardingItem(projectId, obItemId).catch((err) => {
          console.error('Delete onboarding checklist item failed:', err);
          window.alert('Could not delete this checklist item. Try again or refresh the page.');
        });
        return;
      }
      void deleteProjectTask(taskId).catch((err) => {
        console.error('Delete task failed:', err);
        window.alert('Could not delete this task.');
      });
    },
    [deleteProjectTask, deleteOnboardingItem, projectOnboardings],
  );

  const toggleItem = useCallback((id: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const getGroupName = (groupId?: string) => {
    if (!groupId) return '';
    return taskGroups.find(g => g.id === groupId)?.name || '';
  };

  const formatDate = (d: Date) => formatRelativeLocalCalendarDay(d);

  const resolveUserName = useCallback((uid: string) => {
    const u = crmUsers.find(c => c.uid === uid);
    return u?.displayName || 'Someone';
  }, [crmUsers]);

  const sortTasksByAction = useCallback((tasks: ProjectTask[]) => {
    return [...tasks].sort((a, b) => {
      const aMyAction = a.actionRequired === userId && a.status !== 'completed' ? 0 : 1;
      const bMyAction = b.actionRequired === userId && b.status !== 'completed' ? 0 : 1;
      if (aMyAction !== bMyAction) return aMyAction - bMyAction;

      const aOtherAction = a.actionRequired && a.actionRequired !== userId && a.status !== 'completed' ? 0 : 1;
      const bOtherAction = b.actionRequired && b.actionRequired !== userId && b.status !== 'completed' ? 0 : 1;
      if (aOtherAction !== bOtherAction) return aOtherAction - bOtherAction;

      const aCompleted = a.status === 'completed' ? 1 : 0;
      const bCompleted = b.status === 'completed' ? 1 : 0;
      return aCompleted - bCompleted;
    });
  }, [userId]);

  // ── Render task row (shared between concept and project views) ──
  const renderTask = (task: ProjectTask) => {
    const dueDate = task.endDate ? new Date(task.endDate) : null;
    const isOverdue = dueDate && task.status !== 'completed' && isTaskDueDatePastLocalCalendar(dueDate);
    const isMyAction = task.actionRequired === userId && task.status !== 'completed';
    const isExternalAr = task.actionRequired ? isActionRequiredExternal(task.actionRequired) : false;
    const waitingOnLabel = task.actionRequired
      ? isExternalAr
        ? (task.actionRequiredNote || '').trim() || 'External'
        : resolveUserName(task.actionRequired)
      : '';
    const taskIncomplete = task.status !== 'completed';
    const isArMenuOpen = arActionMenuTaskId === task.id;
    const arBadgeClass = isMyAction
      ? 'ap-action-mine'
      : isExternalAr
        ? 'ap-action-external'
        : 'ap-action-other';

    const persistTaskAr = (partial: Partial<ProjectTask>) => {
      void handleUpdateTask(task.id!, partial);
      setArActionMenuTaskId(null);
    };

    const taskGroupCtx = buildGroupContextForTask(task, timberProjects, taskGroups, projectTasks, projectOnboardings);
    const taskHasUnreadComments = taskGroupCtx
      ? groupHasUnread(aggregateGroupComments(taskComments, taskGroupCtx), taskGroupCtx, realUserId, readReceipts)
      : false;
    const boardHref = inspirationBoardHrefForTask(task, timberProjects, enhancedDeals);

    return (
      <div
        key={task.id}
        className={`ap-task-card ${task.status === 'completed' ? 'ap-task-completed' : ''} ${isOverdue ? 'ap-task-overdue' : ''} ${isMyAction ? 'ap-task-my-action' : ''} ${isArMenuOpen ? 'ap-task-card--action-open' : ''}`}
        onClick={() => {
          setSelectedTask(task);
          if (realUserId && task.id) void markTaskAsRead(realUserId, task.id);
        }}
      >
        <div className="ap-task-card-left">
          <h3 className="ap-task-name">{task.title}</h3>
          <div className="ap-task-meta">
            {getGroupName(task.groupId) && (
              <span className="ap-task-group">{getGroupName(task.groupId)}</span>
            )}
          </div>
        </div>
        <div className="ap-task-card-right">
          {taskIncomplete && (
            <div
              className={`ap-task-action-inline-host${isArMenuOpen ? ' ap-task-action-inline-host--open' : ''}`}
              ref={isArMenuOpen ? arActionMenuHostRef : undefined}
              onClick={(e) => e.stopPropagation()}
            >
              {task.actionRequired ? (
                <button
                  type="button"
                  className={`ap-action-badge ap-action-badge--btn ap-action-badge--toolbar ${arBadgeClass}`}
                  title={
                    isMyAction
                      ? `Your action required${task.actionRequiredNote && !isExternalAr ? ` — ${task.actionRequiredNote}` : ''}`
                      : isExternalAr
                        ? (task.actionRequiredNote || 'External — action required')
                        : `Waiting on ${waitingOnLabel}${task.actionRequiredNote ? ` — ${task.actionRequiredNote}` : ''}`
                  }
                  onClick={() => setArActionMenuTaskId((cur) => (cur === task.id ? null : task.id!))}
                >
                  {isMyAction ? (
                    <>
                      <Flag size={10} strokeWidth={2.25} aria-hidden /> Your action
                      {task.actionRequiredNote && !isExternalAr && (
                        <span className="ap-action-note"> — {task.actionRequiredNote}</span>
                      )}
                    </>
                  ) : (
                    <>
                      {isExternalAr ? <Building2 size={10} aria-hidden /> : <User size={10} aria-hidden />}
                      <span className="ap-action-badge__label">
                        {isExternalAr ? waitingOnLabel : `Waiting on ${waitingOnLabel}`}
                      </span>
                      {!isExternalAr && task.actionRequiredNote && (
                        <span className="ap-action-note"> — {task.actionRequiredNote}</span>
                      )}
                    </>
                  )}
                </button>
              ) : (
                <button
                  type="button"
                  className="ap-action-set-inline ap-action-set-inline--toolbar"
                  title="Set action required"
                  onClick={() => setArActionMenuTaskId((cur) => (cur === task.id ? null : task.id!))}
                >
                  <Flag size={10} strokeWidth={2.25} aria-hidden />
                  <span className="ap-action-set-inline__text">Action</span>
                </button>
              )}
              {isArMenuOpen && (
                <div
                  className="pm-dropdown ap-task-action-dropdown"
                  style={{ minWidth: arActionMenuStep === 'external' ? 220 : 180 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {arActionMenuStep === 'external' ? (
                    <div className="pm-action-external-panel" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className="pm-dropdown-item pm-action-external-back"
                        onClick={() => {
                          setArActionMenuStep('list');
                          setArActionExternalDraft('');
                        }}
                      >
                        <ArrowLeft size={12} /> Back
                      </button>
                      <label className="pm-action-external-label">Who we&apos;re waiting on</label>
                      <input
                        className="pm-action-external-input"
                        value={arActionExternalDraft}
                        onChange={(e) => setArActionExternalDraft(e.target.value)}
                        placeholder="e.g. county inspector, vendor"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && arActionExternalDraft.trim()) {
                            persistTaskAr({
                              actionRequired: ACTION_REQUIRED_EXTERNAL_SENTINEL,
                              actionRequiredNote: arActionExternalDraft.trim(),
                            });
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="pm-action-external-save"
                        disabled={!arActionExternalDraft.trim()}
                        onClick={() => {
                          if (!arActionExternalDraft.trim()) return;
                          persistTaskAr({
                            actionRequired: ACTION_REQUIRED_EXTERNAL_SENTINEL,
                            actionRequiredNote: arActionExternalDraft.trim(),
                          });
                        }}
                      >
                        Save
                      </button>
                    </div>
                  ) : (
                    <>
                      {task.actionRequired && (
                        <button
                          type="button"
                          className="pm-dropdown-item"
                          onClick={() => {
                            persistTaskAr({ actionRequired: undefined, actionRequiredNote: undefined } as any);
                          }}
                        >
                          Clear action required
                        </button>
                      )}
                      {teamMembers.map((u) => (
                        <button
                          key={u.uid}
                          type="button"
                          className="pm-dropdown-item"
                          onClick={() => persistTaskAr({ actionRequired: u.uid, actionRequiredNote: undefined })}
                        >
                          <Flag size={10} style={{ color: '#DC2626' }} aria-hidden />
                          {u.displayName}
                        </button>
                      ))}
                      <button
                        type="button"
                        className="pm-dropdown-item pm-dropdown-item--external"
                        onClick={() => {
                          setArActionMenuStep('external');
                          setArActionExternalDraft(
                            task.actionRequired && isActionRequiredExternal(task.actionRequired)
                              ? (task.actionRequiredNote || '').trim()
                              : '',
                          );
                        }}
                      >
                        <Flag size={10} style={{ color: '#047857' }} aria-hidden />
                        External…
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
          <div className="ap-task-card-right-controls" onClick={(e) => e.stopPropagation()}>
            <StatusPill
              status={task.status}
              onChange={s => handleUpdateTask(task.id!, { status: s, progress: s === 'completed' ? 100 : task.progress })}
            />
            <PriorityBadge priority={task.priority} readonly />
            {dueDate && (
              <span className={`ap-task-date ${isOverdue ? 'ap-date-overdue' : ''}`}>
                <Calendar size={11} />
                {formatDate(dueDate)}
              </span>
            )}
            <button
              type="button"
              data-tour="task-comments-btn"
              className={`ap-task-comments-btn${taskHasUnreadComments ? ' ap-task-comments-btn--unread' : ''}`}
              title={taskHasUnreadComments ? 'New comments — open in popup' : 'Open comments in popup'}
              aria-label={taskHasUnreadComments ? 'Open comments — has unread' : 'Open comments'}
              onClick={(e) => {
                e.stopPropagation();
                handleOpenTaskComments(task);
              }}
            >
              <MessageSquare size={14} strokeWidth={1.85} aria-hidden />
              {taskHasUnreadComments && <span className="ap-task-comments-dot" aria-hidden />}
            </button>
            {boardHref && (
              <button
                type="button"
                className="ap-task-files-btn"
                title="Open inspiration board"
                aria-label="Open inspiration board"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(boardHref, { state: { from: '/designer-portal' } });
                }}
              >
                <LayoutGrid size={14} strokeWidth={1.85} aria-hidden />
              </button>
            )}
            {task.projectId && (
              <button
                type="button"
                className="ap-task-files-btn"
                title="Open project files"
                aria-label="Open project files"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/project-hub/${task.projectId}?tab=files`);
                }}
              >
                <FolderOpen size={14} aria-hidden />
              </button>
            )}
          </div>
          <ChevronRight size={16} className="ap-task-arrow" />
        </div>
      </div>
    );
  };

  const renderProjectGroupCard = ({
    project,
    deal,
    myTasks,
    actionCount,
    overdueCount,
    showPmPriority = true,
    includeCompletedTasks = showCompletedTasks,
  }: ProjectGroup & { showPmPriority?: boolean; includeCompletedTasks?: boolean }) => {
    const isExpanded = expandedItems.has(project.id!);
    const activeTasks = myTasks.filter((t) => t.status !== 'completed');
    const completedTaskCount = myTasks.filter((t) => t.status === 'completed').length;
    const expandedTasks = includeCompletedTasks
      ? sortTasksByAction(myTasks)
      : sortTasksByAction(activeTasks);

    const subStage = deal?.designSubStage;
    const subLabel = subStage ? SUBSTAGE_LABELS[subStage] || subStage : null;
    const subColor = subStage ? SUBSTAGE_COLORS[subStage] || '#94A3B8' : '#94A3B8';

    const statusLabel = STATUS_LABELS[project.status] || project.status;
    const statusColor = STATUS_COLORS[project.status] || '#94A3B8';
    const projectPhase = project.phase || 'contracted';
    const isEstimatingPhase = projectPhase === 'estimating';
    const isPreconPhase = projectPhase === 'preconstruction';

    const myActionCount = activeTasks.filter((t) => t.actionRequired === userId).length;
    const otherActionMap = new Map<string, { label: string; count: number; external: boolean }>();
    for (const t of activeTasks) {
      if (t.actionRequired && t.actionRequired !== userId) {
        if (isActionRequiredExternal(t.actionRequired)) {
          const label = (t.actionRequiredNote || '').trim() || 'External';
          const mapKey = `e:${label}`;
          const prev = otherActionMap.get(mapKey);
          if (prev) prev.count += 1;
          else otherActionMap.set(mapKey, { label, count: 1, external: true });
        } else {
          const name = resolveUserName(t.actionRequired);
          const mapKey = `i:${name}`;
          const prev = otherActionMap.get(mapKey);
          if (prev) prev.count += 1;
          else otherActionMap.set(mapKey, { label: name, count: 1, external: false });
        }
      }
    }
    const otherActionEntries = Array.from(otherActionMap.values());
    const hasAnyAction = myActionCount > 0 || otherActionEntries.length > 0;
    const pmRank = showPmPriority && project.id ? pmPriorityRankByProjectId.get(project.id) : undefined;
    const projectCardTitle = getPortalProjectCardTitle(project, deal);

    return (
      <div
        key={project.id}
        className={`ap-project-card ${isExpanded ? 'expanded' : ''} ${myActionCount > 0 ? 'ap-project-my-action' : ''}`}
      >
        <div className="ap-project-header" onClick={() => toggleItem(project.id!)}>
          <div className="ap-project-header-left">
            {showPmPriority && (
              <div
                className="ap-project-priority-col"
                title="PM priority — same position as in Project Hub's work queue (preconstruction and under contract in one list); updates live when your PM changes the queue."
                aria-label={pmRank != null ? `PM priority ${pmRank}, same as Project Hub work queue` : 'PM priority not set'}
              >
                <span className="ap-project-priority-label">PM</span>
                {pmRank != null ? (
                  <span className="ap-project-priority-num">{pmRank}</span>
                ) : (
                  <span className="ap-project-priority-dash">—</span>
                )}
              </div>
            )}
            <div
              className="ap-concept-pip"
              style={{ background: isEstimatingPhase ? '#F59E0B' : isPreconPhase ? subColor : statusColor }}
              title={
                isEstimatingPhase
                  ? 'Estimating'
                  : isPreconPhase
                    ? subLabel || 'Preconstruction'
                    : statusLabel
              }
            />
            <div className="ap-project-info">
              <div className="ap-project-name-row">
                <h3 className="ap-project-name">{projectCardTitle}</h3>
                {actionCount > 0 && (
                  <span className="ap-project-action-badge">
                    <Flag size={9} /> {actionCount}
                  </span>
                )}
              </div>
              {!isExpanded && hasAnyAction && (
                <div className="ap-project-action-summary">
                  {myActionCount > 0 && (
                    <span className="ap-action-badge ap-action-mine">
                      <Flag size={9} /> Your action · {myActionCount} {myActionCount === 1 ? 'task' : 'tasks'}
                    </span>
                  )}
                  {otherActionEntries.map(({ label, count, external }) => (
                    <span
                      key={`${external ? 'e' : 'i'}:${label}`}
                      className={`ap-action-badge ${external ? 'ap-action-external' : 'ap-action-other'}`}
                    >
                      {external ? <Building2 size={9} aria-hidden /> : <User size={9} aria-hidden />}
                      Waiting on {label} · {count}
                    </span>
                  ))}
                </div>
              )}
              <div className="ap-project-meta">
                <span className="ap-concept-type">{project.projectType || project.frameType || ''}</span>
                {project.location && (
                  <span className="ap-project-location">
                    <MapPin size={11} /> {project.location}
                  </span>
                )}
                {(() => {
                  const designer = deal ? getAssignedDesigner(deal, crmUsers) : { uid: '', name: '' };
                  const extArch = deal ? getExternalArchitectName(deal, crmUsers) : '';
                  if (!designer.name && !extArch) return null;
                  return (
                    <>
                      {designer.name && (
                        <span className="ap-concept-architect" title="Designer">
                          <User size={11} /> {designer.name}
                        </span>
                      )}
                      {extArch && (
                        <span className="ap-concept-architect" title="Architect">
                          <Building2 size={11} /> {extArch}
                        </span>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
          <div className="ap-project-header-right">
            <span
              className={`ap-project-phase-chip${
                isEstimatingPhase
                  ? ' ap-project-phase-chip--estimating'
                  : isPreconPhase
                    ? ' ap-project-phase-chip--precon'
                    : ' ap-project-phase-chip--contract'
              }`}
            >
              {isEstimatingPhase ? 'Estimating' : isPreconPhase ? 'Precon' : 'Under Contract'}
            </span>
            <span className="ap-project-task-count">
              {activeTasks.length} active
              {includeCompletedTasks && completedTaskCount > 0 && (
                <span className="ap-project-task-count-done"> · {completedTaskCount} done</span>
              )}
            </span>
            {overdueCount > 0 && (
              <span className="ap-project-overdue-badge">
                <AlertTriangle size={11} /> {overdueCount}
              </span>
            )}
            <ChevronDown size={16} className={`ap-project-chevron ${isExpanded ? 'open' : ''}`} />
          </div>
        </div>

        {isExpanded && (
          <div className="ap-project-tasks">
            {expandedTasks.length > 0 ? (
              expandedTasks.map(renderTask)
            ) : (
              <div className="ap-project-tasks-empty">
                <p>
                  {showPmPriority
                    ? `No tasks assigned to ${viewingAsExternal ? viewAsExternalName : 'you'} on this project.`
                    : `No task history for ${viewingAsExternal ? viewAsExternalName : 'you'} on this project yet.`}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const uploadPanelProjectId = selectedTask?.projectId ?? groupCommentsContext?.primaryProjectId ?? null;
  const uploadPanelDealId = uploadPanelProjectId
    ? timberProjects.find(p => p.id === uploadPanelProjectId)?.dealId
    : undefined;
  const taskDetailProject = selectedTask?.projectId
    ? timberProjects.find(p => p.id === selectedTask.projectId) ?? null
    : null;

  const taskDetailDeal = useMemo(
    () => (taskDetailProject?.dealId ? enhancedDeals.find(d => d.id === taskDetailProject.dealId) : undefined),
    [taskDetailProject?.dealId, enhancedDeals],
  );

  const taskDetailPanelProjectContext = useMemo(
    () => getTaskDetailPanelProjectContext(taskDetailProject, taskDetailDeal, crmUsers),
    [taskDetailProject, taskDetailDeal, crmUsers],
  );

  return (
    <ProjectUploadModalProvider projectId={uploadPanelProjectId} dealId={uploadPanelDealId}>
    <div className="ap-portal ap-combined">
      {isPortalChrome ? (
        <ArchitectHeader
          homePath="/designer-portal"
          homeLinkLabel={isEngineerView ? 'Engineer Work' : 'Design Work'}
          showStandup={!isEngineerView}
          linkSearch={preview ? staffPortalPreviewSearch(preview.kind, preview.uid) : ''}
        />
      ) : (
        <AppHeader />
      )}
      <div className="ap-container">

        <div className="ap-mode-desc-row">
          <p className="ap-mode-desc">
            {isEngineerView
              ? 'Engineering projects together — sorted by PM priority.'
              : 'Preconstruction and under-contract projects together — sorted by PM priority.'}
          </p>
          <div className="ap-mode-desc-actions">
            {user?.role === 'admin' && (
              <Link to="/team" className="ap-admin-tool-btn">
                <Users size={14} aria-hidden />
                Team Management
              </Link>
            )}
            <button
              type="button"
              className="ap-show-completed-btn"
              aria-pressed={showCompletedTasks}
              onClick={() => setShowCompletedTasks((v) => !v)}
            >
              {showCompletedTasks ? 'Hide completed' : 'Show completed'}
            </button>
          </div>
        </div>

        {/* View as external user (employees only) */}
        {canShowViewAs && (
          <div className="ap-view-as">
            <StaffPortalViewAs
              designers={architects}
              architects={partnerArchitects}
              engineers={engineers}
              current={preview ? { kind: preview.kind, uid: preview.uid } : null}
              currentName={viewAsExternalName}
              onChange={(next) => navigate(staffPortalPreviewHref(next.kind, next.uid))}
              onExit={() => navigate(staffPortalPreviewExitHref())}
            />
          </div>
        )}

        {/* Stats */}
        <div className="ap-stats">
          <div className="ap-stat-card ap-stat-action">
            <Flag size={18} />
            <span className="ap-stat-num">{stats.action}</span>
            <span className="ap-stat-label">Action Required</span>
          </div>
          <div className="ap-stat-card">
            <Clock size={18} />
            <span className="ap-stat-num">{stats.tasks}</span>
            <span className="ap-stat-label">My Active Tasks</span>
          </div>
          <div className="ap-stat-card ap-stat-overdue">
            <AlertTriangle size={18} />
            <span className="ap-stat-num">{stats.overdue}</span>
            <span className="ap-stat-label">Overdue</span>
          </div>
          <div className="ap-stat-card">
            <FolderOpen size={18} />
            <span className="ap-stat-num">{stats.total}</span>
            <span className="ap-stat-label">Projects</span>
          </div>
        </div>

        {/* ═══ PRIORITY TASK PROJECTS ═══ */}
        {activeGroupsSorted.length > 0 && (
          <div className="ap-project-priority-legend" role="note">
            <div className="ap-project-priority-legend-row">
              <p className="ap-project-priority-legend-text">
                <span className="ap-project-priority-legend-title">PM priority</span>
                {' — '}
                The number on each card matches its row in Project Hub&apos;s work queue — one list for estimating,
                preconstruction, and under contract together. It updates here automatically whenever your PM changes it.
              </p>
            </div>
          </div>
        )}
        <div className="ap-project-list">
          {activeGroupsSorted.length === 0 ? (
            <div className="ap-empty">
              <FolderOpen size={32} />
              <p>
                {showCompletedTasks
                  ? `No projects with tasks for ${viewingAsExternal ? viewAsExternalName : 'you'}.`
                  : `No projects with active tasks for ${viewingAsExternal ? viewAsExternalName : 'you'}.`}
              </p>
            </div>
          ) : (
            activeGroupsSorted.map((group) =>
              renderProjectGroupCard({ ...group, showPmPriority: true, includeCompletedTasks: showCompletedTasks }),
            )
          )}
        </div>

        {portalExtraGroups.length > 0 && (
          <>
            <div className="ap-project-section-divider" aria-hidden />
            <div className="ap-project-priority-legend ap-project-involvement-legend" role="note">
              <div className="ap-project-priority-legend-row">
                <p className="ap-project-priority-legend-text">
                  <span className="ap-project-priority-legend-title">Your projects</span>
                  {' — '}
                  {isEngineerView
                    ? 'Open projects where you work at the Engineer of Record’s firm, have task history, or were @mentioned in a comment — including projects without active tasks in the list above.'
                    : 'Open projects where you’re the assigned designer, have task history, or were @mentioned in a comment — including projects without active tasks in the list above.'}
                </p>
              </div>
            </div>
            <div className="ap-project-list ap-project-list--involvement">
              {portalExtraGroups.map((group) =>
                renderProjectGroupCard({ ...group, showPmPriority: false, includeCompletedTasks: true }),
              )}
            </div>
          </>
        )}
      </div>

      {selectedTask && (
        <TaskDetailPanel
          task={projectTasks.find(t => t.id === selectedTask.id) || selectedTask}
          teamMembers={teamMembers}
          currentUserId={userId}
          currentUserName={viewAsExternalName || userName}
          showActionRequiredWaitAnalytics={canViewActionRequiredWaitAnalytics(user?.role)}
          onUpdate={handleUpdateTask}
          onDelete={handleDeleteTask}
          onClose={() => setSelectedTask(null)}
          isOnboardingChecklistTask={isOnboardingBridgeTaskId(selectedTask.id)}
          project={taskDetailProject}
          projectContext={taskDetailPanelProjectContext}
        />
      )}

      <GroupCommentsPopout
        groupContext={groupCommentsContext}
        popoutToken={popoutCommentsToken}
        dealId={commentsPopoutDealId}
        projectPdfAnnotations={groupCommentsAnnotations}
        onClose={handleCloseCommentsPopout}
        groupMemberTasks={groupCommentMemberTasks}
        teamMembers={teamMembers}
        currentUserId={userId}
        onUpdateTask={handleUpdateTask}
        onCompleteTask={handleRequestGroupTaskComplete}
        onOpenPdf={(u, t, docId, opts) =>
          navigate(
            `/project-hub/${groupCommentsContext?.primaryProjectId ?? ''}?tab=files&file=${encodeURIComponent(
              docId,
            )}${opts?.annotationId ? `&comment=${encodeURIComponent(opts.annotationId)}` : ''}`,
          )
        }
        onOpenImage={(u, t, docId, opts) =>
          navigate(
            `/project-hub/${groupCommentsContext?.primaryProjectId ?? ''}?tab=files&file=${encodeURIComponent(
              docId,
            )}${opts?.annotationId ? `&comment=${encodeURIComponent(opts.annotationId)}` : ''}`,
          )
        }
        onOpenCsv={(u, t, docId, opts) =>
          navigate(
            `/project-hub/${groupCommentsContext?.primaryProjectId ?? ''}?tab=files&file=${encodeURIComponent(
              docId,
            )}${opts?.annotationId ? `&comment=${encodeURIComponent(opts.annotationId)}` : ''}`,
          )
        }
      />

      {pendingCompletionTask && (
        <CompleteTaskActualsModal
          taskTitle={pendingCompletionTask.title}
          scheduledStart={pendingCompletionTask.scheduledStart}
          scheduledEnd={pendingCompletionTask.scheduledEnd}
          onCancel={() => setPendingCompletionTask(null)}
          onConfirm={({ actualStart, actualEnd }) => {
            void (async () => {
              const ctx = pendingCompletionTask;
              setPendingCompletionTask(null);
              await applyTaskCompletion(ctx, actualStart, actualEnd, {
                actorName: userName || user?.email || 'Unknown',
                updateProjectTask,
                updateOnboardingItem,
              });
            })();
          }}
        />
      )}

      {projectTasks.length > 0 && <CommentsHelpButton onReplay={startCommentsTour} />}
    </div>
    </ProjectUploadModalProvider>
  );
};

export default ArchitectPortal;
